import React, { useCallback, useEffect, useMemo, useState } from 'react';

import currencyFormatter from 'currency-formatter';
import { useSnackbar } from 'notistack';
import { Link as NavLink } from '~/lib/react-router-dom';

import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import { DataGridPro, GridColDef, GridRowParams } from '@mui/x-data-grid-pro';

import { useGetEligibleCreditReversals } from '~/hooks/apis/orders/useGetEligibleCreditReversals';
import { useCreateCreditReversal } from '~/hooks/apis/payments/useCreateCreditReversal';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import type { CreditReversalOrder } from '~/services/OrdersService';
import type { CreateCreditReversalPayload } from '~/services/PaymentsService';
import { formatDateWeekDayShortMonth } from '~/services/TimeService';

import CashPaymentsDetailPanel from './CashPaymentsDetailPanel';
import CreditReversalForm from './CreditReversalForm/CreditReversalForm';
import type { CreditReversalFormFields } from './CreditReversalForm/types';
import { ROW_CLASS_INELIGIBLE } from './constants';
import type { EligibleCreditReversalsModalProps } from './types';

type Step = 'select' | 'form';

function formatMaxReversal(row: CreditReversalOrder, currency: string): string {
  if (row.maxReversal <= 0) {
    return '—';
  }
  const code = row.currency || currency;
  return `${currencyFormatter.format(row.maxReversal, { code, precision: 2 })} ${code}`;
}

const CREDIT_REVERSAL_COLUMNS = (currency: string): Array<GridColDef<CreditReversalOrder>> => [
  {
    field: 'transactionDate',
    headerName: 'Transaction Date',
    sortable: false,
    width: 160,
    valueFormatter: (value: string) => (value ? formatDateWeekDayShortMonth(value) : '—'),
  },
  {
    field: 'orderId',
    headerName: 'Order ID',
    sortable: false,
    width: 280,
    renderCell: (params) => (
      <Link component={NavLink} to={`/purchases/${params.value}`} underline="hover">
        {params.value}
      </Link>
    ),
  },
  {
    field: 'maxReversal',
    headerName: 'Maximum Reversal Amount',
    sortable: false,
    width: 200,
    renderCell: (params) => formatMaxReversal(params.row, currency),
  },
  {
    field: 'packageDetails',
    headerName: 'Package Details',
    sortable: false,
    flex: 1,
    minWidth: 140,
    valueFormatter: (value: string | null) => value ?? '—',
  },
  {
    field: 'bookingId',
    headerName: 'Booking ID',
    sortable: false,
    width: 140,
    valueFormatter: (value: string | null) => value ?? '—',
  },
  {
    field: 'checkIn',
    headerName: 'Check-In',
    sortable: false,
    width: 120,
    valueFormatter: (value: string | null) => value ?? '—',
  },
  {
    field: 'checkOut',
    headerName: 'Check-Out',
    sortable: false,
    width: 120,
    valueFormatter: (value: string | null) => value ?? '—',
  },
];

export default function EligibleCreditReversalsModal({
  currency,
  onClose,
  open,
  userId,
}: EligibleCreditReversalsModalProps) {
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('select');

  const { enqueueSnackbar } = useSnackbar();
  const { tenant } = useCurrentTenant();
  const createCreditReversalMutation = useCreateCreditReversal();

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage, refetch, isRefetching } =
    useGetEligibleCreditReversals(userId, 5, open);

  const rows = useMemo(() => data?.pages.flatMap((p) => p.result) ?? [], [data]);

  const selectedOrder = useMemo(() => rows.find((r) => r.orderId === selectedOrderId) ?? null, [rows, selectedOrderId]);

  const columns = useMemo(() => CREDIT_REVERSAL_COLUMNS(currency), [currency]);

  const handleRowClick = useCallback((params: GridRowParams<CreditReversalOrder>) => {
    if (!params.row.isEligibleForCreditReversal) {
      return;
    }
    setSelectedOrderId((prev) => (prev === params.row.orderId ? null : params.row.orderId));
  }, []);

  useEffect(() => {
    if (!open) {
      setStep('select');
      setSelectedOrderId(null);
    }
  }, [open]);

  const handleNext = useCallback(() => {
    if (selectedOrder) {
      setStep('form');
    }
  }, [selectedOrder]);

  const handleBack = useCallback(() => {
    setStep('select');

    setSelectedOrderId(null);
  }, []);

  const handleFormSubmit = useCallback(
    (formData: CreditReversalFormFields) => {
      const payload: CreateCreditReversalPayload & { submission_notes?: string } = {
        order_id: formData.orderId,
        booking_id: formData.bookingId || undefined,
        case_ticket_number: formData.caseTicketNumber,
        reason: formData.reason as CreateCreditReversalPayload['reason'],
        other_reason: formData.otherReason || undefined,
        is_payment_method_valid: formData.isPaymentMethodValid,
        credit_deduction_amount: formData.creditDeductionAmount,
        cash_reversed_amount: formData.cashReversedAmount,
        currency: formData.currency as CreateCreditReversalPayload['currency'],
        includes_merchant_fee: formData.includesMerchantFee,
        brand: tenant.brand as CreateCreditReversalPayload['brand'],
        submission_notes: formData.submissionNotes || undefined,
      };
      createCreditReversalMutation.mutate(payload, {
        onSuccess: () => {
          enqueueSnackbar('Credit reversal submitted successfully', { variant: 'success' });
          onClose();
        },
        onError: (error) => {
          enqueueSnackbar(error.message ?? 'Failed to submit credit reversal', { variant: 'error' });
        },
      });
    },
    [createCreditReversalMutation, enqueueSnackbar, onClose, tenant.brand],
  );

  const getRowClassName = useCallback((params: { row: CreditReversalOrder }) => {
    return !params.row.isEligibleForCreditReversal ? ROW_CLASS_INELIGIBLE : '';
  }, []);

  const getDetailPanelContent = useCallback(
    ({ row }: { row: CreditReversalOrder }) => <CashPaymentsDetailPanel row={row} />,
    [],
  );

  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  const detailPanelExpandedRowIds = useMemo(() => (selectedOrderId ? [selectedOrderId] : []), [selectedOrderId]);

  const handleDetailPanelExpandedRowIdsChange = useCallback((rowIds: Array<string>) => {
    setSelectedOrderId(rowIds.length > 0 ? rowIds[0] : null);
  }, []);

  const isSelectStep = step === 'select';

  return (
    <Dialog fullWidth maxWidth="xl" onClose={onClose} open={open}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Reverse Credits
        {isSelectStep && (
          <IconButton
            onClick={() => refetch()}
            disabled={isRefetching}
            size="small"
            aria-label="Refresh eligible orders"
            color="primary"
          >
            {isRefetching ? <CircularProgress size={24} /> : <RefreshIcon />}
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
      >
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
          {isSelectStep ? 'Select an order to process credit reversal.' : 'Fill in the reversal details'}
        </Typography>

        {isSelectStep ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Maximum Reversal Amount</strong> is the lesser of the total cash paid and the total amount
              refunded as travel credits for this order, minus any credit reversals already processed. Rows that are not
              eligible are greyed out.
            </Alert>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                '& .credit-reversal-row-ineligible': {
                  opacity: 0.5,
                  backgroundColor: 'action.hover',
                  pointerEvents: 'none',
                },
              }}
            >
              <DataGridPro
                rows={rows}
                columns={columns}
                getRowId={(row) => row.orderId}
                getRowClassName={getRowClassName}
                onRowClick={handleRowClick}
                rowSelectionModel={selectedOrderId ? [selectedOrderId] : []}
                getDetailPanelContent={getDetailPanelContent}
                getDetailPanelHeight={getDetailPanelHeight}
                detailPanelExpandedRowIds={detailPanelExpandedRowIds}
                onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
                loading={isLoading && rows.length === 0}
                hideFooterPagination
                disableColumnFilter
                disableColumnMenu
              />
              {hasNextPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <Button variant="text" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                    {isFetchingNextPage ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                    {isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </Button>
                </Box>
              )}
            </Box>
          </>
        ) : (
          selectedOrder && (
            <CreditReversalForm key={selectedOrder.orderId} order={selectedOrder} onSubmit={handleFormSubmit} />
          )
        )}
      </DialogContent>
      <DialogActions>
        {isSelectStep ? (
          <>
            <Button onClick={onClose} variant="text">
              Cancel
            </Button>
            <Button color="primary" disabled={!selectedOrderId} onClick={handleNext} variant="contained">
              Next
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleBack} variant="text">
              Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={onClose} variant="text">
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={createCreditReversalMutation.isPending}
              type="submit"
              form="credit-reversal-form"
              variant="contained"
            >
              {createCreditReversalMutation.isPending ? 'Submitting…' : 'Submit Reversal'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
